import { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const SCOUT_LIKE_ROLES = ["scout", "coach", "club", "admin", "super_admin", "moderator", "verification_admin"];

// Lightweight cache for which athletes the current user has favourited
let cachedFavorites = null;
const listeners = new Set();
async function loadCache() {
  if (cachedFavorites !== null) return cachedFavorites;
  try {
    const { data } = await api.get("/favorites");
    cachedFavorites = new Set((data.favorites || []).map(f => f.athlete_id));
  } catch { cachedFavorites = new Set(); }
  return cachedFavorites;
}
function notify() { listeners.forEach(fn => fn(new Set(cachedFavorites))); }

export function useFavorites() {
  const [favorites, setFavorites] = useState(cachedFavorites || new Set());
  useEffect(() => {
    let active = true;
    loadCache().then(c => active && setFavorites(new Set(c)));
    const fn = (next) => setFavorites(next);
    listeners.add(fn);
    return () => { active = false; listeners.delete(fn); };
  }, []);
  return favorites;
}

export default function FavoriteButton({ athleteId, size = 16, className = "", showLabel = false }) {
  const { user } = useAuth();
  const favorites = useFavorites();
  const [busy, setBusy] = useState(false);
  const allowed = SCOUT_LIKE_ROLES.includes(user?.role);
  if (!allowed) return null;
  const isFav = favorites.has(athleteId);

  const toggle = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (isFav) {
        await api.delete(`/favorites/${athleteId}`);
        cachedFavorites.delete(athleteId);
        toast.success("Removed from shortlist");
      } else {
        await api.post(`/favorites/${athleteId}`);
        cachedFavorites.add(athleteId);
        toast.success("Added to shortlist");
      }
      notify();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={isFav ? "Remove from shortlist" : "Add to shortlist"}
      className={`inline-flex items-center gap-1.5 ${isFav ? "text-[#F85037]" : "text-zinc-400 hover:text-[#F85037]"} transition-colors ${className}`}
      data-testid={`favorite-btn-${athleteId}`}
    >
      <Heart size={size} fill={isFav ? "#F85037" : "none"} strokeWidth={1.75} />
      {showLabel && <span className="text-xs font-medium">{isFav ? "Shortlisted" : "Shortlist"}</span>}
    </button>
  );
}
